import React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

const portfolio = [
  {
    src: "/portfolio/closets.jpg",
    alt: "Closet build-outs and organization portfolio",
    label: "Furniture & Equipment Assembly",
    quote:
      "Willie did an excellent job assembling an indoor sauna for us. He showed up on time and was very professional. Would highly recommend and definitely use again.",
  },
  {
    src: "/portfolio/beds.jpg",
    alt: "Bed and bunk bed assembly portfolio",
    label: "Bed & Bunk Bed Assembly",
    quote:
      "Willie was fantastic! I had a large kids bunk bed with a slide, drawers, and stairs. He did awesome work — 10 out of 10! Pleasant, professional, and wonderful to work with!",
  },
  {
    src: "/portfolio/shelving.jpg",
    alt: "Custom shelving installation portfolio",
    label: "Art & Picture Hanging",
    quote:
      "Willie came 100% on time and his work is impeccable. He hung my mirrors, adjusted my painting and advised me on the best screws so my pieces won’t fall off the wall.",
  },
  {
    src: "/portfolio/art.jpg",
    alt: "Art and picture hanging portfolio — gallery walls and framed prints",
    label: "Custom Shelving & Mounting",
    quote:
      "Willie was very professional and detailed. He suggested I hang things on a different wall and I couldn’t be happier with his suggestion. I will most definitely book Willie again.",
  },
  {
    src: "/portfolio/assembly.jpg",
    alt: "Furniture & equipment assembly portfolio — saunas, gazebos, trampolines, gym equipment",
    label: "Closet Build-Outs & Organization",
    quote:
      "I recently had the pleasure of having Willie complete some work in my home — he was superb in every aspect. Strong work ethic, polite, respectful, and cleaned up thoroughly.",
  },
];

function Stars() {
  return (
    <div className="flex items-center gap-0.5 text-amber-400">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-amber-400" strokeWidth={0} />
      ))}
    </div>
  );
}

export default function HeroPortfolioCarousel() {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true });

  React.useEffect(() => {
    if (!embla) return undefined;
    const id = setInterval(() => embla.scrollNext(), 6000);
    return () => clearInterval(id);
  }, [embla]);

  return (
    <div className="relative pf-fade-in" data-testid="hero-portfolio-carousel">
      <div className="absolute -inset-4 bg-blue-100/40 rounded-3xl blur-xl" />
      <div
        className="relative overflow-hidden rounded-3xl shadow-xl"
        ref={emblaRef}
      >
        <div className="flex">
          {portfolio.map((p, i) => (
            <div
              key={p.src}
              className="relative shrink-0 grow-0 basis-full"
              data-testid={`portfolio-slide-${i}`}
            >
              <img
                src={p.src}
                alt={p.alt}
                className="w-full h-[520px] object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-2xl p-5 shadow-lg">
                <div className="flex items-center justify-between gap-3">
                  <Stars />
                  <span className="text-xs uppercase tracking-[0.18em] font-bold text-blue-700">
                    {p.label}
                  </span>
                </div>
                <p className="mt-3 text-sm md:text-base font-medium text-slate-800 leading-relaxed">
                  “{p.quote}”
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        aria-label="Previous photo"
        onClick={() => embla?.scrollPrev()}
        data-testid="portfolio-prev"
        className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-md flex items-center justify-center"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Next photo"
        onClick={() => embla?.scrollNext()}
        data-testid="portfolio-next"
        className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-amber-400 hover:bg-amber-500 text-slate-900 shadow-md flex items-center justify-center"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
}
